import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import experimentation from "@pcln/experimentation";
import { Dialog } from "pcln-design-system";
import { useEffect } from "react";
import ChatContent from "./ChatContent.js";
import { DialogContentWrapper } from "./components/DialogContentWrapper.js";
import ChatMessageErrorBoundary from "./components/ErrorBoundary/ChatMessageErrorBoundary.js";
import ErrorComponent from "./components/ErrorComponent/index.js";
import { DefaultModalHeader } from "./components/Header/DefaultModalHeader.js";
import GoogleMapComponents from "./components/Maps/GoogleMapComponents.js";
import PennyCaresSurvey from "./components/Survey/PennyCaresSurvey.js";
import { ChatProvider } from "./context/ChatProvider.js";
import { DialogStyleProvider } from "./context/DialogStyleProvider.js";
import { useAlertDialogState } from "./hooks/useAlertDialogState.js";
import { useChatbotState } from "./hooks/useChatBotState.js";
import { useChatMessagesState } from "./hooks/useChatMessagesState.js";
import { useChatUIState } from "./hooks/useChatUIState.js";
import { useLoadingState } from "./hooks/useLoadingState.js";
import useLoadPreviousPrompts from "./hooks/useLoadPreviousPrompts.js";
import usePromptRetrieval from "./hooks/usePromptRetrieval.js";
import { useRealTimeState } from "./hooks/useRealTimeState.js";
import { useSuppressOnNativeWebView } from "./hooks/useSuppressOnNativeWebView.js";
import RealTimeAudioPanel from "./RealTimeAudioPanel.js";
import { browserStorageManager } from "./utils/browserStorage.js";
import { isOfferSurveyDone } from "./utils/pennyCaresSurvey.js";
const PROMPT_LIMIT = 10;
const PclnUcChatBot = (props)=>{
    const { componentVersion, discoveryCitySelected, handleMarkerResponse, isChatBoxOpened, locale = 'en-us', markerComponents = {}, onChatDialogRendered, onClose, realtimeContext, resolvedConfig, resolvedDisplay, resolvedFeatures, resolvedPayload, chatbotFeatures } = props;
    const { shouldSuppress } = useSuppressOnNativeWebView();
    const { requestFrom, debugMode, fetchRequestOptions, retrieveUrl } = resolvedConfig ?? {};
    const browserStorage = browserStorageManager.initBrowserStorage(requestFrom);
    const { setiValues = {}, quickActionsStackVariant, isCaresSurveyEligible } = resolvedFeatures ?? {};
    const { isRealtime, isCaresSurveyEnabled } = setiValues;
    const { chatContainerHeight, dialogOverlayProps } = resolvedDisplay ?? {};
    const { submitChatMessagesPayload, isMobile } = resolvedPayload;
    const { productId, cguid, hotelPayload, isSessionActive } = submitChatMessagesPayload;
    const offerNumber = hotelPayload?.offerNumber;
    const pennyCareSurveyDone = 'undefined' != typeof window ? isOfferSurveyDone(offerNumber) : false;
    const isCaresSurveyEligibleAndEnabled = isCaresSurveyEligible && isCaresSurveyEnabled && !pennyCareSurveyDone;
    const { logAnalytics, speechFetchRequestOptions } = resolvedConfig || {};
    const { logChatInitialization, logCustomerCarePhoneNumberClicked, logCustomerCarePhoneNumberDisplayed, logFirstMessageSubmitted } = logAnalytics;
    const { chatMessages, setChatMessages, santizedSubmitChatMessagesPayload, setSanitizedSubmitChatMessagesPayload } = useChatMessagesState({
        submitChatMessagesPayload,
        resolvedFeatures
    });
    const { isLoading, setLoadingStatus, retrievalLoading, setRetrievalLoading, loadingStatusMessages, setLoadingStatusMessage, loadingHotelSkeleton, setLoadingHotelSkeleton } = useLoadingState();
    const { startRealTime, setStartRealTime } = useRealTimeState({
        isRealtime
    });
    const isAlertDialogActive = useAlertDialogState();
    const { showSurvey, setShowSurvey, openErrorComponent, setOpenErrorComponent, isMapOpened, setIsMapOpened } = useChatUIState();
    const { audioPlayerRef, loadMoreMessages, setLoadMoreMessages, lastPromptId, setLastPromptId } = useChatbotState({
        ...props
    });
    const visibilityChannel = new BroadcastChannel('chatbot-session-events');
    const enableVoice = setiValues?.enableVoice;
    const enableVoiceTooltip = setiValues?.enableVoiceTooltip;
    useEffect(()=>{
        const handleMessage = (event)=>{
            const { count } = event.data;
            if (browserStorage) {
                browserStorage.updateUserMessageCount(count);
                if (count > 0) browserStorage.setInitialMessageSubmitted();
            }
        };
        visibilityChannel.addEventListener('message', handleMessage);
        return ()=>{
            visibilityChannel.removeEventListener('message', handleMessage);
        };
    }, []);
    useEffect(()=>{
        const updateMessageCountAsync = async ()=>{
            const userChatMessagesCount = chatMessages.filter((message)=>'user' === message.role && '' !== message.content).length;
            visibilityChannel.postMessage({
                count: userChatMessagesCount
            });
        };
        updateMessageCountAsync().catch((e)=>{});
    }, [
        chatMessages
    ]);
    useEffect(()=>{
        if (isChatBoxOpened) {
            logChatInitialization();
            onChatDialogRendered();
            if (!resolvedConfig?.customerName && 'homePage' === requestFrom) experimentation.fireImpression('CHATBOT_VOICE_REALTIME_SIGNED_OUT');
        }
    }, [
        isChatBoxOpened,
        resolvedConfig?.customerName,
        onChatDialogRendered
    ]);
    const { shouldRetrieveOnHover } = setiValues;
    const { retrievePrompts } = usePromptRetrieval({
        retrieveUrl,
        cguid,
        lastPromptId,
        setChatMessages,
        setRetrievalLoading,
        retrievalLoading,
        setLastPromptId,
        limit: PROMPT_LIMIT,
        isSessionActive: isSessionActive ?? true,
        productId,
        requestFrom,
        chatMessages,
        triggerOnLoad: !shouldRetrieveOnHover
    });
    useEffect(()=>{
        const handleRetrievePrompts = ()=>{
            if (shouldRetrieveOnHover) retrievePrompts();
        };
        const channel = new BroadcastChannel('penny-chatbot-events');
        channel.addEventListener('message', handleRetrievePrompts);
        return ()=>{
            channel.removeEventListener('message', handleRetrievePrompts);
            channel.close();
        };
    }, [
        shouldRetrieveOnHover,
        retrievePrompts
    ]);
    useLoadPreviousPrompts({
        retrieveUrl,
        cguid,
        lastPromptId,
        limit: PROMPT_LIMIT,
        loadMoreMessages,
        setChatMessages,
        setRetrievalLoading,
        setLastPromptId,
        setLoadMoreMessages,
        isSessionActive: isSessionActive ?? true,
        retrievalLoading
    });
    if (shouldSuppress) return null;
    return /*#__PURE__*/ jsx(ChatProvider, {
        chatbotData: {
            audioPlayerRef,
            chatContainerHeight,
            componentVersion,
            debugMode,
            discoveryCitySelected,
            enableVoice,
            enableVoiceTooltip,
            fetchRequestOptions,
            handleMarkerResponse,
            isDev: resolvedConfig?.isDev,
            isMobile,
            isRealtime,
            isSessionActive: true,
            loadMoreMessages,
            locale,
            logCustomerCarePhoneNumberClicked,
            logCustomerCarePhoneNumberDisplayed,
            logFirstMessageSubmitted,
            markerComponents,
            quickActionsStackVariant,
            requestFrom,
            setiValues,
            setLoadMoreMessages,
            shouldDisplayGenHotelFeature: setiValues?.shouldDisplayGenHotelFeature,
            showLandingPage: resolvedConfig?.showLandingChat ?? false,
            speechFetchRequestOptions,
            submitChatMessagesPayload: santizedSubmitChatMessagesPayload,
            transcriptionFetchRequestOptions: resolvedConfig?.transcriptionFetchRequestOptions || {},
            chatbotFeatures
        },
        chatMessages: {
            chatMessages,
            setChatMessages,
            santizedSubmitChatMessagesPayload,
            setSanitizedSubmitChatMessagesPayload
        },
        loadingState: {
            isLoading,
            setLoadingStatus,
            retrievalLoading,
            setRetrievalLoading,
            loadingStatusMessages,
            setLoadingStatusMessage,
            loadingHotelSkeleton,
            setLoadingHotelSkeleton
        },
        ui: {
            showSurvey,
            setShowSurvey,
            openErrorComponent,
            setOpenErrorComponent,
            isMapOpened,
            setIsMapOpened,
            shouldDisplayMapButton: true
        },
        realtime: {
            setStartRealTime,
            startRealTime,
            isRealtime
        },
        children: /*#__PURE__*/ jsxs(DialogStyleProvider, {
            children: [
                /*#__PURE__*/ jsx("audio", {
                    id: "penny-audio-player",
                    "data-testid": "penny-audio-player",
                    ref: audioPlayerRef,
                    children: /*#__PURE__*/ jsx("track", {
                        kind: "captions"
                    })
                }),
                /*#__PURE__*/ jsx(Dialog, {
                    showCloseButton: false,
                    scrimDismiss: !startRealTime,
                    open: isChatBoxOpened,
                    size: "lg",
                    onOpenChange: function(isOpen) {
                        if (!isOpen) onClose();
                    },
                    borderRadius: isMobile ? 'lg' : '2xl',
                    shouldDisableTabIndex: true,
                    ...dialogOverlayProps,
                    children: /*#__PURE__*/ jsx(DialogContentWrapper, {
                        isAlertDialogActive: isAlertDialogActive,
                        ...dialogOverlayProps,
                        chatContainerHeight: chatContainerHeight,
                        children: startRealTime ? /*#__PURE__*/ jsx(RealTimeAudioPanel, {
                            locale: locale,
                            realtimeContext: realtimeContext,
                            isModal: true,
                            requestFrom: requestFrom,
                            isCustomVoice: setiValues?.isCustomVoice ?? false
                        }) : /*#__PURE__*/ jsxs(Fragment, {
                            children: [
                                /*#__PURE__*/ jsx(DefaultModalHeader, {
                                    onClose: function() {
                                        if (!showSurvey && isCaresSurveyEligibleAndEnabled) setShowSurvey(true);
                                        else {
                                            setShowSurvey(false);
                                            onClose();
                                        }
                                    },
                                    speechEnabled: setiValues?.enableVoice
                                }),
                                isMapOpened ? /*#__PURE__*/ jsx(ChatMessageErrorBoundary, {
                                    compnentName: "GoogleMapComponents",
                                    FallbackComponent: /*#__PURE__*/ jsx(ErrorComponent, {}),
                                    children: /*#__PURE__*/ jsx(GoogleMapComponents, {
                                        setiValues: setiValues
                                    })
                                }) : /*#__PURE__*/ jsx(ChatMessageErrorBoundary, {
                                    FallbackComponent: /*#__PURE__*/ jsx(ErrorComponent, {}),
                                    children: isCaresSurveyEligibleAndEnabled && showSurvey ? /*#__PURE__*/ jsx(PennyCaresSurvey, {
                                        onClose: onClose,
                                        offerNumber: offerNumber
                                    }) : /*#__PURE__*/ jsx(ChatContent, {
                                        retrievalLoading: retrievalLoading,
                                        visibilityChannel: visibilityChannel
                                    })
                                })
                            ]
                        })
                    })
                })
            ]
        })
    });
};
export { PROMPT_LIMIT, PclnUcChatBot };
